import { TableRow, Dropdown } from 'semantic-ui-react';
import { useParams } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';
import { COLORS, Row, Separator, FlexView } from '@cuemath/comps';
import { Button, Text } from '@cuemath/web-comps';

import { getFormattedDateWithDay, getTimeDistance } from 'utils';
import Pagination from 'modules/list/pagination';
import { Heading, Table, Filters, ColoredChip, TableCell, TableInfoCell } from 'comps';

import { useSessionsGetAll } from 'actions/studentsv2';
import { useAppContext } from '../../../app-context';
import ExternalLinkImg from 'img/left-nav/external-link.svg';
import styled from 'styled-components';
import { trackEvent } from 'modules/analytics';
import { EVENT_TYPE } from 'constants/logger';

const StyledButton = styled(Button)`
  img {
    margin-right: 8px;
  }
`;

const StyledDiv = styled.div`
  :hover {
    text-decoration: underline;
    cursor: pointer;
  }
`;

const isEqualList = (arr1, arr2) =>
  arr1?.length === arr2?.length && arr1?.every((val, index) => val === arr2[index]);

const DEMO_TYPE = { EVAL: 'EVAL' };
const SLOT_TAG = { DEMO: 'DEMO', EVAL: 'EVAL' };

// Evals ride on the DEMO slot tag; demo_type is what distinguishes them.
const getSessionTag = (tag, demoDetails) =>
  tag === SLOT_TAG.DEMO && demoDetails?.demo_type === DEMO_TYPE.EVAL
    ? SLOT_TAG.EVAL
    : tag;

function getClassTypeTitle(type) {
  if (type === 'regular') {
    return 'Regular Class';
  } else if (type === 'ptm') {
    return 'PTM';
  } else if (type === 'demo') {
    return 'Demo Class';
  } else if (type === 'eval') {
    return 'Eval Class';
  } else if (type === 'extra') {
    return 'Extra Class';
  } else if (type === 'teacher_training') {
    return 'Teacher Training Class';
  } else {
    return type;
  }
}

const statusOptions = [
  { key: 'ABSENT', value: 'ABSENT', text: 'ABSENT' },
  { key: 'COMPLETED', value: 'COMPLETED', text: 'COMPLETED' },
  { key: 'IN_FUTURE', value: 'IN_FUTURE', text: 'IN_FUTURE' },
  { key: 'ONGOING', value: 'ONGOING', text: 'ONGOING' },
  { key: 'TEACHER_ABSENT', value: 'TEACHER_ABSENT', text: 'TEACHER_ABSENT' },
];

const typeOptions = [
  { key: 'DEMO', value: 'DEMO', text: 'DEMO' },
  { key: 'EXTRA', value: 'EXTRA', text: 'EXTRA' },
  { key: 'PTM', value: 'PTM', text: 'PTM' },
  { key: 'REGULAR', value: 'REGULAR', text: 'REGULAR' },
  { key: 'SETUP', value: 'SETUP', text: 'SETUP' },
  { key: 'TEST', value: 'TEST', text: 'TEST' },
  { key: 'TEACHER_TRAINING', value: 'TEACHER_TRAINING', text: 'TEACHER TRAINING' },
];

export default function Sessions({ userId }) {
  const { setSidePanelView } = useAppContext();
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const [sessionType, setSetSessionType] = useState([]);
  const [sessionStatus, setSetSessionStatus] = useState([]);
  const prevFilterDataRef = useRef({
    slot_tags: sessionType,
    class_status: sessionStatus,
    page,
  });
  const {
    data: sessions,
    getAll: getSessions,
    isStale,
    isProcessing,
  } = useSessionsGetAll(id);
  const { student_classrooms: studentClassroom, total } = sessions || {};

  const [filterOptions, setFilterOptions] = useState({
    page: 1,
    per_page: 13,
    include_demo_details: true,
    include_ptm_details: true,
    include_course_class_logs: true,
    include_teacher_details: true,
  });

  useEffect(() => {
    getSessions(id, filterOptions);

    trackEvent(EVENT_TYPE.SESSION_SCREEN_VIEW, {
      student_id: id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSessions, id]);

  useEffect(() => {
    const { current } = prevFilterDataRef;
    const {
      class_status: prevSessionStatus,
      slot_tags: prevSessionType,
      page: prevPage,
    } = current || {};

    if (prevFilterDataRef && prevFilterDataRef.current) {
      let options = { ...filterOptions };
      if (!isEqualList(prevSessionStatus, sessionStatus)) {
        options = { ...options, class_status: sessionStatus };
      }
      if (prevPage !== page) {
        options = { ...options, page };
      }
      if (!isEqualList(prevSessionType, sessionType)) {
        options = { ...options, slot_tags: sessionType };
      }
      if (
        !isEqualList(prevSessionStatus, sessionStatus) ||
        !isEqualList(prevSessionType, sessionType) ||
        prevPage !== page
      ) {
        setFilterOptions({ ...options });
        getSessions(id, { ...options });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sessionStatus, sessionType, id]);

  useEffect(() => {
    if (isStale && !isProcessing) {
      getSessions(id, filterOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStale, id]);

  const onPageChange = newPage => {
    prevFilterDataRef.current.page = page;
    setPage(newPage);
  };

  const handleAddTypeFilter = (key, data) => {
    const { value } = data || {};
    prevFilterDataRef.current.slot_tags = [...(sessionType || [])];

    setSetSessionType(value);
  };

  const handleAddStatusFilter = (key, data) => {
    const { value } = data || {};
    prevFilterDataRef.current.class_status = [...(sessionStatus || [])];
    setSetSessionStatus(value);
  };

  const onViewDemoClick = () => {
    window.open(`/student-form-parent/${id}/true`, '_blank');

    trackEvent(EVENT_TYPE.VIEW_DEMO_SLOTS, {
      student_id: id,
    });
  };

  const onInfoClick = (session, tag) => {
    setSidePanelView({
      view: 'session-info',
      props: { ...session },
      title: getClassTypeTitle(tag?.toLowerCase()),
    });

    trackEvent(EVENT_TYPE.SESSION_INFO_CLICK, {
      class_type: tag,
      student_id: id,
    });
  };

  const openTeacherProfile = teacherId => {
    window.open(`/users/teachers/${teacherId}`, '_blank');
    trackEvent(EVENT_TYPE.TEACHER_NAME_CLICK, {
      student_id: id,
      teacher_id: teacherId,
    });
  };

  return (
    <>
      <Row justifyContent="space-between" alignItems="center">
        <Heading>Sessions Info</Heading>
        <StyledButton
          backgroundColor="transparent"
          size="xsmall"
          label="View demo slots"
          iconConfig={{ imgLink: ExternalLinkImg }}
          onClick={onViewDemoClick}
        />
      </Row>
      <FlexView flexDirection="row" alignItems="center" justifyContent="space-between">
        <Filters filters={[]} />
        <Text color={COLORS.COLA_LIGHTER}>{`${
          page === 1 ? (total === 0 ? 0 : 1) : (page - 1) * 13 + 1
        }-${Math.min(total, page * 13)} of ${total}`}</Text>
      </FlexView>
      <Table
        headerCell={[
          { title: 'date & day' },
          { title: 'time' },
          { title: 'teacher' },
          {
            title: 'type',
            filterApplied: sessionType?.length,
            filterComponent: (
              <Dropdown
                clearable
                fluid
                multiple
                onChange={handleAddTypeFilter}
                options={typeOptions}
                placeholder="Select type"
                search
                selection
                value={sessionType}
              />
            ),
            showFilters: true,
          },
          {
            title: 'status',
            filterApplied: sessionStatus?.length,
            filterComponent: (
              <Dropdown
                clearable
                fluid
                multiple
                onChange={handleAddStatusFilter}
                options={statusOptions}
                placeholder="Select status"
                search
                selection
                value={sessionStatus}
              />
            ),
            showFilters: true,
          },
          { title: 'plan' },
        ]}
        showLoader={isProcessing}
        havePagination
        infoWidth="32px"
        dataLength={studentClassroom?.length}
      >
        {studentClassroom?.map(session => {
          const {
            id: sessionId,
            tag,
            class_status: classStatus,
            class_end_ts: classEndTime,
            class_start_ts: classStartTime,
            date,
            teacher_details: teacherDetails,
            is_invalid: isInvalid,
            classroom_profile: classRoomProfile,
            demo_details: demoDetails,
          } = session;

          const displayTag = getSessionTag(tag, demoDetails);

          const { enrollment_type: enrollmentType, class_ratio: classRatio } =
            classRoomProfile || {};

          const { name: teacherName, id: teacherId } = teacherDetails || {};
          const isEnrolled = !!enrollmentType;
          const haveClassRatio = !!classRatio;

          const enrollmentTypeText =
            isEnrolled && haveClassRatio
              ? `${enrollmentType} 1:${classRatio}`
              : isEnrolled && !classRatio
              ? enrollmentType
              : null;

          return (
            <TableRow key={sessionId}>
              <TableInfoCell onClick={() => onInfoClick(session, displayTag)} width={1} />
              <TableCell width={2}>{getFormattedDateWithDay(date) || '-'} </TableCell>
              <TableCell width={2.5}>
                {getTimeDistance(classStartTime, classEndTime) || '-'}
              </TableCell>
              <TableCell width={3}>
                {teacherName ? (
                  <StyledDiv onClick={() => openTeacherProfile(teacherId)}>
                    {teacherName}
                  </StyledDiv>
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell width={2.5}>
                {displayTag ? (
                  <ColoredChip
                    color={(displayTag.toLowerCase() === 'extra' || displayTag.toLowerCase() === 'teacher_training') && COLORS.WHITE}
                    variant={displayTag.toLowerCase() + '_tag'}
                  >
                    {displayTag}
                  </ColoredChip>
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell width={2.5}>
                {isInvalid
                  ? 'Invalid'
                  : classStatus?.toLowerCase().replace(/\b\w/g, m => m.toUpperCase()) ||
                    '-'}
              </TableCell>
              <TableCell width={3}>
                {enrollmentTypeText ? (
                  <ColoredChip
                    color={COLORS.WHITE}
                    variant={enrollmentType.toLowerCase()}
                  >
                    {enrollmentTypeText}
                  </ColoredChip>
                ) : (
                  '-'
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </Table>
      <Separator transparent height={8} />
      {studentClassroom && total > 13 && (
        <Row justifyContent="center" alignItems="center">
          <Pagination
            page={page}
            perPage={13}
            count={total}
            onPageChange={onPageChange}
            shouldShowNewUi
          />
        </Row>
      )}
    </>
  );
}
